import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { useState, useMemo } from 'react';
import { Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DeletePixelModal } from './DeletePixelModal';
import { PixelTableDesktop } from './PixelTableDesktop';
import { PixelCardMobile } from './PixelCardMobile';
import { PixelKPIs } from './PixelKPIs';
import { AddPixelData, Pixel } from '@/types/pixel';

interface MeusPixelsTabProps {
  pixels: Pixel[];
  videos: Array<{ id_video: string; nomeVideo: string }>;
  addPixel: (data: AddPixelData) => Promise<void>;
  deletePixel: (pixelId: string) => Promise<void>;
}

/**
 * Aba somente-leitura: lista, filtra e remove.
 * Criação exclusiva via aba Configurações (que informa a plataforma).
 * `addPixel`/`videos` mantidos na assinatura por compat (não usados).
 */
export function MeusPixelsTab({ pixels, deletePixel }: MeusPixelsTabProps) {
  const language = useLanguage();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [pixelToDelete, setPixelToDelete] = useState<Pixel | null>(null);

  const handleDeletePixel = async () => {
    if (pixelToDelete) {
      try {
        await deletePixel(pixelToDelete.id);
        setPixelToDelete(null);
      } catch (error) {
        // Erro já foi tratado no hook
        console.error('Error deleting pixel:', error);
      }
    }
  };

  // Calculate KPIs
  const kpis = useMemo(() => {
    const activePixels = pixels.filter((p) => p.status === 'ativo').length;
    const uniqueVideoIds = new Set(pixels.flatMap((p) => p.flowId ? p.coveredVideoIds || [] : p.videoId ? [p.videoId] : []));
    const trackedVideos = uniqueVideoIds.size;

    // Última atividade exige evidência real de evento.
    const dates = pixels
      .map((p) => {
        const dateStr = p.lastEventAt;
        if (!dateStr) return null;
        try {
          return new Date(dateStr);
        } catch {
          return null;
        }
      })
      .filter((d): d is Date => d !== null && !isNaN(d.getTime()));

    const mostRecent = dates.length > 0 ? new Date(Math.max(...dates.map(d => d.getTime()))) : null;
    const lastActivity = mostRecent ? mostRecent.toLocaleDateString(language) : null;

    return { activePixels, trackedVideos, lastActivity };
  }, [pixels, language]);

  const filteredPixels = pixels.filter((pixel) => {
    const matchesSearch =
      searchQuery === '' ||
      (pixel.token && pixel.token.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (pixel.siteUrl && pixel.siteUrl.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (pixel.videoName && pixel.videoName.toLowerCase().includes(searchQuery.toLowerCase()));

    const matchesStatus = statusFilter === 'all' || pixel.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  return (
    <div>
      {/* KPIs */}
      <PixelKPIs
        activePixels={kpis.activePixels}
        trackedVideos={kpis.trackedVideos}
        lastActivity={kpis.lastActivity}
      />

      {/* Filters (sem botão criar: criação só em Configurações) */}
      <div className="mb-6">
        <h3 className="text-sm font-semibold text-[#111] mb-3">{translateInterfaceText("Filtros")}</h3>
        <div className="grid grid-cols-1 md:grid-cols-[1.5fr_1fr] gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
            <Input
              placeholder={translateInterfaceText("Buscar por URL, token, vídeo ou funil")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-[42px] rounded-[10px] border-[#E2E8F0]"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-[42px] rounded-[10px] border-[#E2E8F0]">
              <SelectValue placeholder={translateInterfaceText("Todos os status")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{translateInterfaceText("Todos os status")}</SelectItem>
              <SelectItem value="ativo">{translateInterfaceText("Ativo")}</SelectItem>
              <SelectItem value="desativado">{translateInterfaceText("Desativado")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Content */}
      {filteredPixels.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <h3 className="text-lg font-semibold text-[#111] mb-2">{translateInterfaceText("Nenhum pixel encontrado")}</h3>
          <p className="text-sm text-[#666] mb-6">
            {pixels.length === 0
              ? translateInterfaceText("Vá até a aba Configurações para cadastrar seu primeiro pixel.")
              : translateInterfaceText("Nenhum pixel corresponde aos filtros selecionados.")}
          </p>
        </div>
      ) : (
        <>
          {/* Desktop Table */}
          <div className="hidden md:block">
            <PixelTableDesktop
              pixels={filteredPixels}
              onRemove={setPixelToDelete}
            />
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden space-y-3">
            {filteredPixels.map((pixel) => (
              <PixelCardMobile
                key={pixel.id}
                pixel={pixel}
                onRemove={setPixelToDelete}
              />
            ))}
          </div>
        </>
      )}

      {/* Modals */}
      <DeletePixelModal
        open={!!pixelToDelete}
        onOpenChange={() => setPixelToDelete(null)}
        onConfirm={handleDeletePixel}
        isFlow={!!pixelToDelete?.flowId}
      />
    </div>
  );
}
